import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchBookings, type Booking } from "@/lib/bookings";
import {
  DataTable,
  ErrorBanner,
  FilterBar,
  LoadingState,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  btnGhost,
  btnPrimary,
  type Column,
  type Tone,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { exportToCsv, exportToPdf } from "@/pages/admin/exportUtils";
import { summarizeBookings } from "@/lib/adminReports";

const BOOKING_STATUS: Record<string, { label: string; tone: Tone }> = {
  pending: { label: "Pending", tone: "accent" },
  confirmed: { label: "Confirmed", tone: "primary" },
  completed: { label: "Completed", tone: "secondary" },
  cancelled: { label: "Cancelled", tone: "neutral" },
};

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

export default function AdminBookingReports() {
  const [rows, setRows] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await fetchBookings());
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load bookings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const typeOptions = useMemo(() => {
    const types = Array.from(new Set(rows.map((row) => row.booking_type || "general"))).sort();
    return [{ value: "all", label: "All types" }, ...types.map((type) => ({ value: type, label: type }))];
  }, [rows]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (typeFilter !== "all" && (row.booking_type || "general") !== typeFilter) return false;
      const date = row.preferred_date || "";
      if (from && date && date < from) return false;
      if (to && date && date > to) return false;
      if (!query) return true;
      return [row.name, row.email || "", row.subject || "", row.booking_type || ""]
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [rows, search, statusFilter, typeFilter, from, to]);

  const summary = useMemo(() => summarizeBookings(filtered), [filtered]);

  const exportColumns = [
    { header: "Reference", value: (row: Booking) => `#${row.id}` },
    { header: "Guest", value: (row: Booking) => row.name },
    { header: "Email", value: (row: Booking) => row.email || "" },
    { header: "Phone", value: (row: Booking) => row.phone || "" },
    { header: "Type", value: (row: Booking) => row.booking_type || "general" },
    { header: "Subject", value: (row: Booking) => row.subject || "" },
    { header: "Preferred date", value: (row: Booking) => row.preferred_date || "" },
    { header: "Group size", value: (row: Booking) => `${row.group_size || 0}` },
    { header: "Status", value: (row: Booking) => BOOKING_STATUS[row.status]?.label || row.status },
    { header: "Source page", value: (row: Booking) => row.source_page || "" },
  ];

  const columns: Column<Booking>[] = [
    {
      key: "guest",
      header: "Guest",
      primary: true,
      render: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
          <p className="text-xs text-foreground-500 mt-0.5">#{row.id} · {row.booking_type || "general"}</p>
        </div>
      ),
    },
    {
      key: "subject",
      header: "Experience",
      render: (row) => <span className="text-sm text-foreground-700">{row.subject || "—"}</span>,
    },
    {
      key: "date",
      header: "Preferred date",
      render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{formatDate(row.preferred_date)}</span>,
    },
    {
      key: "guests",
      header: "Guests",
      render: (row) => <span className="text-sm text-foreground-700">{row.group_size || 0}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (row) => {
        const meta = BOOKING_STATUS[row.status] || { label: row.status, tone: "neutral" as Tone };
        return <StatusPill label={meta.label} tone={meta.tone} />;
      },
    },
    {
      key: "contact",
      header: "Contact",
      render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.phone || row.email || "—"}</span>,
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Reports"
        title="Booking Reports"
        subtitle="Every booking request with status, type, group size and source — filter the view, then hand the list off as CSV or PDF."
        actions={
          <>
            <button type="button" className={btnGhost} onClick={() => exportToCsv("rythugaru-bookings", exportColumns, filtered)}>
              <i className="ri-file-excel-2-line"></i> CSV
            </button>
            <button type="button" className={btnPrimary} onClick={() => exportToPdf({ title: "Booking Report", subtitle: `${filtered.length} bookings`, columns: exportColumns, rows: filtered })}>
              <i className="ri-file-pdf-2-line"></i> PDF
            </button>
          </>
        }
      />

      <StatCards
        stats={[
          { label: "Bookings in view", value: summary.total, icon: "ri-calendar-check-line", tone: "primary" },
          { label: "Confirmed", value: summary.confirmed, icon: "ri-checkbox-circle-line", tone: "primary" },
          { label: "Pending", value: summary.pending, icon: "ri-time-line", tone: "accent" },
          { label: "Total guests", value: summary.guests, icon: "ri-group-line", tone: "secondary" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search guest, email, experience…" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={STATUS_OPTIONS} />
          <Select label="Type" value={typeFilter} onChange={setTypeFilter} options={typeOptions} />
          <label className="inline-flex items-center gap-2 text-sm text-foreground-700">
            <span className="hidden sm:inline whitespace-nowrap">From</span>
            <input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              className="bg-background-100 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            />
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-foreground-700">
            <span className="hidden sm:inline whitespace-nowrap">To</span>
            <input
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              className="bg-background-100 border border-background-300 rounded-md px-3 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            />
          </label>
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} bookings</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading bookings…" />
        ) : (
          <DataTable columns={columns} rows={filtered} />
        )}
      </div>
    </div>
  );
}